/**
 * IndexedDB-backed state machine for message-request gating.
 *
 * Two artefacts persist in a single IndexedDB database ("kaether_mr"):
 *
 *   - per-userId record  → object store "records"
 *     Holds the MessageRequestRecord (state + counters + preview).
 *     Key: sha256(userId) hex string, matching the iOS "mr_<hash>.json" scheme.
 *
 *   - contact roster     → object store "roster"
 *     A flat set of userIds that bypass the gate. Populated by
 *     markContact(userId) (explicit) or accept(userId) (implicit when the
 *     user accepts a pending request).
 *
 * State machine (driven by observe(senderUserId, plaintext)):
 *
 *   (no record) + isContact=true  ────▶ no record created          → render
 *   (no record) + isContact=false ────▶ create requested(count=1)  → requestBanner
 *   requested   + observe         ────▶ count++, preview/lastMsg refreshed → requestBanner
 *   accepted    + observe         ────▶ count++ (informational)    → render
 *   blocked     + observe         ────▶ blockedAttemptCount++      → suppress
 *
 * Manual transitions:
 *
 *   any        + accept   ─▶ accepted (+ added to contact roster)
 *   any        + block    ─▶ blocked  (- removed from contact roster)
 *   blocked    + unblock  ─▶ requested (counters preserved; user re-evaluates)
 *   any        + delete   ─▶ record removed (- removed from contact roster
 *                             only if state was accepted; blocked is
 *                             remembered via roster-entry absence + future
 *                             observe-creates-fresh-requested)
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/MessageRequestStore.swift` exactly.
 */

import {
  messageRequestPreview,
  MessageRequestNotFoundError,
  MessageRequestInvalidTransitionError,
  Render,
  Suppress,
  type MessageRequestRecord,
  type MessageRequestDisposition,
  type MessageRequestState,
} from "./MessageRequest";

const DB_NAME = "kaether_mr";
const DB_VERSION = 1;
const STORE_RECORDS = "records";
const STORE_ROSTER = "roster";

/**
 * Open (or create) the IndexedDB backing store.
 */
function openDB(dbName = DB_NAME): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = (ev) => {
      const db = (ev.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_RECORDS)) {
        db.createObjectStore(STORE_RECORDS);
      }
      if (!db.objectStoreNames.contains(STORE_ROSTER)) {
        db.createObjectStore(STORE_ROSTER);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Compute a SHA-256 hex digest of a UTF-8 string.
 * Used as the stable storage key for per-userId records (mirrors iOS hashHex).
 */
async function hashHex(userId: string): Promise<string> {
  const bytes = new TextEncoder().encode(userId);
  const buf = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function idbGet<T>(
  db: IDBDatabase,
  storeName: string,
  key: string,
): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const req = tx.objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(
  db: IDBDatabase,
  storeName: string,
  key: string,
  value: unknown,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const req = tx.objectStore(storeName).put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbDelete(
  db: IDBDatabase,
  storeName: string,
  key: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const req = tx.objectStore(storeName).delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbGetAll<T>(db: IDBDatabase, storeName: string): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  });
}

function idbGetAllKeys(
  db: IDBDatabase,
  storeName: string,
): Promise<IDBValidKey[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const req = tx.objectStore(storeName).getAllKeys();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Clear all entries from a store (used by resetAll). */
function idbClear(db: IDBDatabase, storeName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const req = tx.objectStore(storeName).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ─── Serialised wire type for IndexedDB ────────────────────────────────────

/** Stored shape: same fields as MessageRequestRecord but with explicit type. */
type StoredRecord = Omit<MessageRequestRecord, never>;

// ─── MessageRequestStore ───────────────────────────────────────────────────

/**
 * IndexedDB-backed state machine for message-request gating.
 *
 * Every public method is async; the underlying IndexedDB calls are awaited
 * internally. Callers do not need to manage database lifecycle — the DB is
 * opened lazily on first use and reused for the lifetime of the store instance.
 *
 * For test isolation pass a unique `dbName` per test.
 */
export class MessageRequestStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName = DB_NAME) {
    this.dbName = dbName;
  }

  /** Lazy DB open — reuses the connection once established. */
  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDB(this.dbName);
    }
    return this.dbPromise;
  }

  // ── Storage helpers ────────────────────────────────────────────────────

  private async readRecord(userId: string): Promise<MessageRequestRecord | null> {
    const db = await this.db();
    const key = await hashHex(userId);
    const stored = await idbGet<StoredRecord>(db, STORE_RECORDS, key);
    return stored ?? null;
  }

  private async writeRecord(record: MessageRequestRecord): Promise<void> {
    const db = await this.db();
    const key = await hashHex(record.userId);
    await idbPut(db, STORE_RECORDS, key, record);
  }

  private async deleteRecord(userId: string): Promise<void> {
    const db = await this.db();
    const key = await hashHex(userId);
    await idbDelete(db, STORE_RECORDS, key);
  }

  private async allRecords(): Promise<MessageRequestRecord[]> {
    const db = await this.db();
    return idbGetAll<MessageRequestRecord>(db, STORE_RECORDS);
  }

  // ── Roster helpers ─────────────────────────────────────────────────────

  private async isContactInternal(userId: string): Promise<boolean> {
    const db = await this.db();
    const val = await idbGet<string>(db, STORE_ROSTER, userId);
    return val !== undefined;
  }

  private async addToRoster(userId: string): Promise<void> {
    const db = await this.db();
    await idbPut(db, STORE_ROSTER, userId, userId);
  }

  private async removeFromRoster(userId: string): Promise<void> {
    const db = await this.db();
    await idbDelete(db, STORE_ROSTER, userId);
  }

  // ── Drive state from incoming messages ─────────────────────────────────

  /**
   * Called for every successfully-decrypted incoming message AFTER any
   * receipt-payload short-circuit (we don't want delivery/read receipts
   * triggering message requests).
   *
   * Returns the disposition the renderer should respect. Side effects:
   *   - creates / updates the IndexedDB record per the state machine
   *   - never throws (this is on the message-receive hot path; if IndexedDB
   *     is broken, the worst that happens is we forget a pending request, not
   *     lose a message)
   *
   * Mirrors `-[MessageRequestStore observe:plaintext:at:]`.
   */
  async observe(
    senderUserId: string,
    plaintext: string,
    at: number = Date.now(),
  ): Promise<MessageRequestDisposition> {
    try {
      const preview = messageRequestPreview(plaintext);
      const inRoster = await this.isContactInternal(senderUserId);
      const existing = await this.readRecord(senderUserId);

      if (existing === null && inRoster) {
        // Known contact's first message — no gating, no record.
        return Render;
      }

      if (existing === null && !inRoster) {
        // Stranger's first message — create a requested record.
        const record: MessageRequestRecord = {
          userId: senderUserId,
          state: "requested",
          firstSeenAt: at,
          lastMessageAt: at,
          messageCount: 1,
          previewSnippet: preview,
          blockedAttemptCount: 0,
        };
        await this.writeRecord(record);
        return { kind: "requestBanner", record };
      }

      if (existing!.state === "requested") {
        // Bump counters; refresh preview and lastMessageAt.
        const bumped: MessageRequestRecord = {
          ...existing!,
          lastMessageAt: at,
          messageCount: existing!.messageCount + 1,
          previewSnippet: preview,
          blockedAttemptCount: 0,
        };
        await this.writeRecord(bumped);
        return { kind: "requestBanner", record: bumped };
      }

      if (existing!.state === "accepted") {
        // Update counter + lastMessageAt for accurate ordering, but don't
        // surface a banner. Preview is intentionally NOT recorded here —
        // once accepted, the live chat is the source of truth.
        const bumped: MessageRequestRecord = {
          ...existing!,
          lastMessageAt: at,
          messageCount: existing!.messageCount + 1,
          previewSnippet: null,
          blockedAttemptCount: 0,
        };
        await this.writeRecord(bumped);
        return Render;
      }

      // blocked
      const bumped: MessageRequestRecord = {
        ...existing!,
        lastMessageAt: at,
        messageCount: existing!.messageCount + 1,
        previewSnippet: null,
        blockedAttemptCount: existing!.blockedAttemptCount + 1,
      };
      await this.writeRecord(bumped);
      return Suppress;
    } catch {
      // Never throw on the receive hot path.
      return Render;
    }
  }

  // ── Manual transitions ─────────────────────────────────────────────────

  /**
   * User accepts a pending request (or explicitly approves a contact). Adds
   * to the contact roster so future first-messages bypass the gate. If no
   * record exists yet, creates one in 'accepted' directly — useful for
   * "tap to start a chat with this username" flows.
   */
  async accept(
    userId: string,
    at: number = Date.now(),
  ): Promise<MessageRequestRecord> {
    await this.addToRoster(userId);
    const existing = await this.readRecord(userId);
    const next: MessageRequestRecord = {
      userId,
      state: "accepted",
      firstSeenAt: existing?.firstSeenAt ?? at,
      lastMessageAt: existing?.lastMessageAt ?? at,
      messageCount: existing?.messageCount ?? 0,
      previewSnippet: null,
      blockedAttemptCount: 0,
    };
    await this.writeRecord(next);
    return next;
  }

  /**
   * User blocks a peer. Removes them from the contact roster. May be called
   * with no prior record — creates a 'blocked' record directly so we still
   * have a place to count incoming attempts.
   */
  async block(
    userId: string,
    at: number = Date.now(),
  ): Promise<MessageRequestRecord> {
    await this.removeFromRoster(userId);
    const existing = await this.readRecord(userId);
    const next: MessageRequestRecord = {
      userId,
      state: "blocked",
      firstSeenAt: existing?.firstSeenAt ?? at,
      lastMessageAt: existing?.lastMessageAt ?? at,
      messageCount: existing?.messageCount ?? 0,
      previewSnippet: null,
      blockedAttemptCount: 0,
    };
    await this.writeRecord(next);
    return next;
  }

  /**
   * Move a 'blocked' record back to 'requested'. Counters are kept so the
   * user can see "blocked X tried 17 times" history if a UI ever surfaces
   * that. Throws if there's no record or it's not blocked.
   */
  async unblock(
    userId: string,
    _at: number = Date.now(),
  ): Promise<MessageRequestRecord> {
    const existing = await this.readRecord(userId);
    if (!existing) {
      throw new MessageRequestNotFoundError();
    }
    if (existing.state !== "blocked") {
      throw new MessageRequestInvalidTransitionError(existing.state, "unblock");
    }
    const next: MessageRequestRecord = {
      ...existing,
      state: "requested",
      blockedAttemptCount: 0,
    };
    await this.writeRecord(next);
    return next;
  }

  /**
   * Wipe the record entirely. If the previous state was 'accepted', also
   * remove from the contact roster — otherwise the next observe would render
   * straight through, defeating the user's intent to reset the relationship.
   * 'blocked' records' roster entry is already absent so deletion of a
   * blocked record produces a fresh 'requested' on the next message (matching
   * Signal's behaviour where deleting a blocked thread doesn't unblock).
   */
  async delete(userId: string): Promise<void> {
    const existing = await this.readRecord(userId);
    if (existing?.state === "accepted") {
      await this.removeFromRoster(userId);
    }
    await this.deleteRecord(userId);
  }

  // ── Contact roster ─────────────────────────────────────────────────────

  /**
   * Mark a user as a known contact without changing any record state. Future
   * first-messages from them will not create a request banner.
   */
  async markContact(userId: string): Promise<void> {
    await this.addToRoster(userId);
  }

  /**
   * Remove a user from the contact roster. Existing records are not modified
   * — an 'accepted' record will still cause render. Use block() if you want
   * to re-engage the gate; unmarkContact is for housekeeping.
   */
  async unmarkContact(userId: string): Promise<void> {
    await this.removeFromRoster(userId);
  }

  async isContact(userId: string): Promise<boolean> {
    return this.isContactInternal(userId);
  }

  // ── Queries ────────────────────────────────────────────────────────────

  async get(userId: string): Promise<MessageRequestRecord | null> {
    return this.readRecord(userId);
  }

  /**
   * Every record currently in 'requested', sorted by lastMessageAt
   * descending — i.e. the requests-inbox order.
   */
  async allRequested(): Promise<MessageRequestRecord[]> {
    const all = await this.allRecords();
    return all
      .filter((r) => r.state === "requested")
      .sort((a, b) => b.lastMessageAt - a.lastMessageAt);
  }

  /** Every blocked userId. Order is unspecified. */
  async allBlocked(): Promise<string[]> {
    const all = await this.allRecords();
    return all.filter((r) => r.state === "blocked").map((r) => r.userId);
  }

  /** Every userId in the contact roster. For diagnostics / sync. */
  async contactRoster(): Promise<string[]> {
    const db = await this.db();
    const keys = await idbGetAllKeys(db, STORE_ROSTER);
    return (keys as string[]).sort();
  }

  // ── Reset (testing / "delete my account") ─────────────────────────────

  /** Wipe a single record (non-destructive alias for delete, skips roster). */
  async reset(userId: string): Promise<void> {
    await this.deleteRecord(userId);
  }

  /** Wipe all records and the entire contact roster. */
  async resetAll(): Promise<void> {
    const db = await this.db();
    await idbClear(db, STORE_RECORDS);
    await idbClear(db, STORE_ROSTER);
  }

  /**
   * Close the underlying IndexedDB connection and delete the entire database.
   * Used by test teardown to free resources and allow re-open with a clean
   * slate without interference from the cached dbPromise.
   */
  async dropDatabase(): Promise<void> {
    // Close any open connection first.
    if (this.dbPromise) {
      const db = await this.dbPromise;
      db.close();
      this.dbPromise = null;
    }
    await new Promise<void>((resolve, reject) => {
      const req = indexedDB.deleteDatabase(this.dbName);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
      req.onblocked = () => resolve(); // another tab has it open — best effort
    });
  }
}

/** Module-level shared singleton — mirrors iOS `MessageRequestStore.shared`. */
export const messageRequestStore = new MessageRequestStore();

/**
 * State type re-exported for convenience so callers only need to import from
 * this module.
 */
export type { MessageRequestState };
